/**
 * Atmosphere + hazard FX for the home bird game.
 * Easy: living spring meadow (meadowAtmosphere — decorative only).
 * Medium: breezy afternoon landscape (breezyAtmosphere) + wind gusts.
 * Hard: rain + lightning strikes (hits share the zap meter).
 */

import {
  clearMeadow,
  drawMeadow,
  seedMeadow,
} from './meadowAtmosphere';
import {
  clearBreezy,
  drawBreezy,
  seedBreezy,
} from './breezyAtmosphere';

export function createWeatherState() {
  return {
    meadow: null,
    breezy: null,
    clouds: [],
    rain: [],
    windPuffs: [],
    sunPhase: 0,
    windGustUntil: 0,
    nextWindAt: 0,
    windLines: [],
    nextLightningAt: 0,
    lightning: null, // { x, until, hitBird }
    boltFlashUntil: 0,
    initedFor: null,
  };
}

function seedClouds(state, boardW, boardH, breezy) {
  state.clouds = [];
  const count = breezy ? 6 : 5;
  for (let i = 0; i < count; i += 1) {
    state.clouds.push({
      x: boardW * Math.random(),
      y: boardH * (0.06 + Math.random() * 0.32),
      w: boardW * (0.16 + Math.random() * 0.2),
      h: boardH * (0.07 + Math.random() * 0.06),
      // Breezy: faster drift so wind reads clearly
      speed: breezy ? 0.85 + Math.random() * 1.1 : 0.35 + Math.random() * 0.55,
      // Breezy clouds need stronger alpha — white-on-white was invisible
      alpha: breezy ? 150 + Math.random() * 60 : 55 + Math.random() * 45,
    });
  }
}

function seedWindPuffs(state, boardW, boardH) {
  state.windPuffs = [];
  for (let i = 0; i < 14; i += 1) {
    state.windPuffs.push({
      x: Math.random() * boardW,
      y: boardH * (0.18 + Math.random() * 0.65),
      len: boardW * (0.08 + Math.random() * 0.1),
      speed: 2.2 + Math.random() * 2.8,
      wobble: Math.random() * Math.PI * 2,
      alpha: 90 + Math.random() * 80,
      thick: 1.5 + Math.random() * 1.8,
    });
  }
}

function seedRain(state, boardW, boardH) {
  state.rain = [];
  const count = 75;
  for (let i = 0; i < count; i += 1) {
    state.rain.push({
      x: Math.random() * boardW,
      y: Math.random() * boardH,
      len: 10 + Math.random() * 16,
      speed: 7 + Math.random() * 9,
      // Stronger wind angle — rain reads as driven leftward
      drift: 0.32 + Math.random() * 0.22,
      alpha: 75 + Math.random() * 95,
    });
  }
}

export function ensureWeather(state, difficulty, boardW, boardH) {
  if (!difficulty) return;
  if (state.initedFor === difficulty.id) {
    if (
      difficulty.weather === 'sunshine' &&
      state.meadow &&
      (Math.abs(state.meadow.w - boardW) > 48 ||
        Math.abs(state.meadow.h - boardH) > 36)
    ) {
      seedMeadow(state, boardW, boardH);
    }
    if (
      difficulty.weather === 'breezy' &&
      state.breezy &&
      (Math.abs(state.breezy.w - boardW) > 48 ||
        Math.abs(state.breezy.h - boardH) > 36)
    ) {
      seedBreezy(state, boardW, boardH);
    }
    return;
  }
  state.initedFor = difficulty.id;
  clearMeadow(state);
  clearBreezy(state);
  state.clouds = [];
  state.rain = [];
  state.windPuffs = [];
  state.lightning = null;
  state.windGustUntil = 0;
  state.windLines = [];
  state.boltFlashUntil = 0;
  state.nextLightningAt = Date.now() + (difficulty.lightningFirstDelayMs || 3500);
  state.nextWindAt = difficulty.windGustChance
    ? Date.now() + (difficulty.windFirstDelayMs || 700)
    : 0;

  if (difficulty.weather === 'sunshine') {
    seedMeadow(state, boardW, boardH);
  } else if (difficulty.weather === 'breezy') {
    seedBreezy(state, boardW, boardH);
  } else if (difficulty.weather === 'storm') {
    seedClouds(state, boardW, boardH, false);
    seedRain(state, boardW, boardH);
    if (difficulty.windGustChance) {
      seedWindPuffs(state, boardW, boardH);
      // Storm wind streaks move faster / denser
      for (let i = 0; i < state.windPuffs.length; i += 1) {
        state.windPuffs[i].speed *= 1.35;
        state.windPuffs[i].alpha = Math.min(200, state.windPuffs[i].alpha + 30);
      }
    }
    // Darker, denser storm clouds
    for (let i = 0; i < state.clouds.length; i += 1) {
      state.clouds[i].alpha = 90 + Math.random() * 50;
      state.clouds[i].speed = 0.55 + Math.random() * 0.7;
      state.clouds[i].y = boardH * (0.02 + Math.random() * 0.28);
    }
  }
}

export function resetWeather(state) {
  state.initedFor = null;
  clearMeadow(state);
  clearBreezy(state);
  state.clouds = [];
  state.rain = [];
  state.windPuffs = [];
  state.lightning = null;
  state.windGustUntil = 0;
  state.nextWindAt = 0;
  state.windLines = [];
  state.boltFlashUntil = 0;
  state.nextLightningAt = 0;
}

export function difficultyHasWind(difficulty) {
  return !!(difficulty && difficulty.windGustChance > 0);
}

function spawnWindLines(state) {
  // Streaks enter from the right and rush left (same way the bird is pushed)
  state.windLines = [];
  for (let i = 0; i < 14; i += 1) {
    state.windLines.push({
      x: 0.65 + Math.random() * 0.35,
      y: 0.12 + Math.random() * 0.75,
      life: 1,
      len: 0.16 + Math.random() * 0.14,
      thick: 2 + Math.random() * 2.2,
    });
  }
}

function scheduleNextWind(state, difficulty, now) {
  const interval = difficulty.windIntervalMs || 1400;
  const jitter = difficulty.windIntervalJitterMs || 600;
  state.nextWindAt = now + interval * (0.75 + Math.random() * 0.5) + Math.random() * jitter * 0.4;
  state.windGustUntil = state.nextWindAt;
}

/** Timed ambient gusts — fires even when the player isn't pressing keys. */
export function tickAmbientWind(state, difficulty, now, reducedMotion) {
  if (!difficultyHasWind(difficulty) || reducedMotion) {
    return { gust: false };
  }
  if (!state.nextWindAt) {
    state.nextWindAt = now + (difficulty.windFirstDelayMs || 700);
  }
  if (now < state.nextWindAt) return { gust: false };
  spawnWindLines(state);
  scheduleNextWind(state, difficulty, now);
  return { gust: true };
}

/** Extra gust chance on a move (on top of ambient timer). */
export function tickWindGust(state, difficulty, now, reducedMotion) {
  if (!difficultyHasWind(difficulty) || reducedMotion) {
    return { gust: false };
  }
  if (now < state.windGustUntil) return { gust: false };

  const chance = difficulty.windGustChance || 0.28;
  if (Math.random() > chance) {
    state.windGustUntil = now + Math.min(400, (difficulty.windGustCooldownMs || 800) * 0.4);
    return { gust: false };
  }

  spawnWindLines(state);
  scheduleNextWind(state, difficulty, now);
  return { gust: true };
}

/**
 * Advance storm hazards. Returns whether lightning hit the bird this frame.
 * @returns {{ lightningHit: boolean }}
 */
export function tickStorm(state, difficulty, boardW, boardH, birdCx, birdCy, now, reducedMotion) {
  if (!difficulty || difficulty.weather !== 'storm') {
    return { lightningHit: false };
  }

  // Rain always drifts when present (angled by wind)
  for (let i = 0; i < state.rain.length; i += 1) {
    const d = state.rain[i];
    const drift = d.drift != null ? d.drift : 0.35;
    if (!reducedMotion) {
      d.y += d.speed;
      d.x -= d.speed * drift;
    }
    if (d.y > boardH + 20) {
      d.y = -20;
      d.x = Math.random() * boardW;
    }
    if (d.x < -20) d.x = boardW + 10;
  }

  if (state.lightning && now >= state.lightning.until) {
    state.lightning = null;
  }

  if (now < state.nextLightningAt || state.lightning) {
    return { lightningHit: false };
  }

  const interval = difficulty.lightningIntervalMs || 5200;
  const jitter = difficulty.lightningJitterMs || 2800;
  state.nextLightningAt = now + interval * (0.7 + Math.random() * 0.6) + Math.random() * jitter;

  const strikeX = boardW * (0.12 + Math.random() * 0.76);
  const hitPad = difficulty.lightningHitPad || 56;
  const hitBird = Math.abs(birdCx - strikeX) <= hitPad && birdCy < boardH * 0.92;

  state.lightning = {
    x: strikeX,
    until: now + (difficulty.lightningFlashMs || 280),
    hitBird,
  };
  state.boltFlashUntil = now + (difficulty.lightningFlashMs || 280);

  return { lightningHit: hitBird };
}

function drawCloud(p, c, storm) {
  p.push();
  p.noStroke();
  const x = c.x;
  const y = c.y;
  const w = c.w;
  const h = c.h;
  if (storm) {
    p.fill(45, 52, 70, c.alpha);
    p.ellipse(x + 4, y + 6, w * 1.08, h * 1.15);
    p.fill(58, 66, 85, c.alpha);
  } else {
    // Soft gray body + brighter rim so clouds read on the light board
    p.fill(165, 180, 200, Math.floor(c.alpha * 0.55));
    p.ellipse(x + 3, y + 4, w * 1.05, h * 1.1);
    p.fill(245, 248, 252, c.alpha);
  }
  p.ellipse(x, y, w, h);
  p.ellipse(x - w * 0.28, y + h * 0.1, w * 0.55, h * 0.75);
  p.ellipse(x + w * 0.3, y + h * 0.08, w * 0.5, h * 0.7);
  p.ellipse(x + w * 0.05, y - h * 0.25, w * 0.45, h * 0.65);
  p.pop();
}

function drawLightningBolt(p, x, boardH, alpha) {
  p.push();
  p.stroke(255, 245, 180, alpha);
  p.strokeWeight(3);
  p.noFill();
  const top = boardH * 0.02;
  const mid = boardH * 0.42;
  const bot = boardH * 0.78;
  p.beginShape();
  p.vertex(x, top);
  p.vertex(x - 10, mid * 0.55);
  p.vertex(x + 6, mid * 0.55);
  p.vertex(x - 14, mid);
  p.vertex(x + 8, mid);
  p.vertex(x - 6, bot);
  p.endShape();
  p.stroke(255, 255, 255, Math.floor(alpha * 0.7));
  p.strokeWeight(1.5);
  p.line(x + 2, top + 8, x - 4, mid * 0.5);
  p.pop();
}

export function drawWeather(
  p,
  state,
  difficulty,
  boardW,
  boardH,
  t,
  now,
  reducedMotion,
  mouseX,
  mouseY
) {
  if (!difficulty || !difficulty.weather) return;

  // Drift clouds left with the wind (bird is pushed toward the start)
  if (state.clouds.length && !reducedMotion) {
    for (let i = 0; i < state.clouds.length; i += 1) {
      const c = state.clouds[i];
      c.x -= c.speed;
      if (c.x + c.w < -40) c.x = boardW + c.w;
    }
  }

  if (difficulty.weather === 'sunshine') {
    drawMeadow(p, state, boardW, boardH, t, reducedMotion, mouseX, mouseY);
    return;
  }

  if (difficulty.weather === 'breezy') {
    const gustStrength = state.windLines.length
      ? Math.min(1, state.windLines.length / 10)
      : 0;
    drawBreezy(
      p,
      state,
      boardW,
      boardH,
      t,
      reducedMotion,
      mouseX,
      mouseY,
      gustStrength
    );

    // Gust burst overlays — keep readable when wind pushes the bird
    if (state.windLines.length) {
      p.push();
      p.strokeCap(p.ROUND);
      const next = [];
      for (let i = 0; i < state.windLines.length; i += 1) {
        const w = state.windLines[i];
        w.life -= reducedMotion ? 0.07 : 0.028;
        if (w.life <= 0) continue;
        const y = boardH * w.y;
        const travel = (1 - w.life) * 0.55;
        const x0 = boardW * (w.x - travel);
        const x1 = x0 - boardW * (w.len || 0.2);
        p.strokeWeight(w.thick || 2.5);
        p.stroke(70, 105, 140, Math.floor(200 * w.life));
        p.line(x0, y, x1, y + 3);
        p.strokeWeight((w.thick || 2.5) * 0.6);
        p.stroke(120, 155, 185, Math.floor(140 * w.life));
        p.line(x0 - 8, y - 5, x1 + 6, y - 2);
        next.push(w);
      }
      state.windLines = next;
      p.pop();
    }
    return;
  }

  if (difficulty.weather === 'storm') {
    // Deep navy / charcoal storm wash
    p.push();
    p.noStroke();
    p.fill(28, 38, 58, 72);
    p.rect(0, 0, boardW, boardH);
    // Distant horizon mist (ocean depth)
    p.fill(45, 60, 80, 55);
    p.ellipse(boardW * 0.35, boardH * 0.92, boardW * 0.9, boardH * 0.28);
    p.fill(50, 70, 95, 40);
    p.ellipse(boardW * 0.75, boardH * 0.88, boardW * 0.55, boardH * 0.22);
    p.pop();

    for (let i = 0; i < state.clouds.length; i += 1) {
      drawCloud(p, state.clouds[i], true);
    }

    // Slow fog sheets between sky and ground
    p.push();
    p.noStroke();
    const fogShift = reducedMotion ? 0 : Math.sin(t * 0.01) * boardW * 0.04;
    p.fill(70, 85, 105, 28);
    p.ellipse(boardW * 0.25 + fogShift, boardH * 0.55, boardW * 0.7, boardH * 0.35);
    p.fill(55, 70, 90, 22);
    p.ellipse(boardW * 0.7 - fogShift * 0.6, boardH * 0.48, boardW * 0.55, boardH * 0.3);
    p.pop();

    p.push();
    p.strokeWeight(1.4);
    for (let i = 0; i < state.rain.length; i += 1) {
      const d = state.rain[i];
      const drift = d.drift != null ? d.drift : 0.35;
      p.stroke(175, 200, 225, d.alpha);
      p.line(d.x, d.y, d.x - d.len * drift * 1.1, d.y + d.len);
    }
    p.pop();

    // Storm wind streaks — same leftward blow as medium
    if (state.windPuffs && state.windPuffs.length) {
      p.push();
      p.noFill();
      p.strokeCap(p.ROUND);
      for (let i = 0; i < state.windPuffs.length; i += 1) {
        const w = state.windPuffs[i];
        if (!reducedMotion) {
          w.x -= w.speed;
          w.y += Math.sin(t * 0.05 + w.wobble) * 0.55;
          if (w.x < -40) {
            w.x = boardW + w.len;
            w.y = boardH * (0.18 + Math.random() * 0.65);
          }
        }
        p.strokeWeight(w.thick);
        p.stroke(150, 175, 210, w.alpha);
        const y2 = w.y + 4;
        p.line(w.x, w.y, w.x - w.len, y2);
        p.strokeWeight(Math.max(1, w.thick * 0.7));
        p.arc(w.x - w.len, y2, 10, 10, Math.PI - 1.2, Math.PI + 0.2, p.OPEN);
      }
      p.pop();
    }

    if (state.windLines.length) {
      p.push();
      p.strokeCap(p.ROUND);
      const next = [];
      for (let i = 0; i < state.windLines.length; i += 1) {
        const w = state.windLines[i];
        w.life -= reducedMotion ? 0.07 : 0.028;
        if (w.life <= 0) continue;
        const y = boardH * w.y;
        const travel = (1 - w.life) * 0.55;
        const x0 = boardW * (w.x - travel);
        const x1 = x0 - boardW * (w.len || 0.2);
        p.strokeWeight(w.thick || 2.5);
        p.stroke(180, 200, 230, Math.floor(220 * w.life));
        p.line(x0, y, x1, y + 3);
        next.push(w);
      }
      state.windLines = next;
      p.pop();
    }

    if (state.lightning && now < state.lightning.until) {
      const fade = (state.lightning.until - now) / (difficulty.lightningFlashMs || 280);
      // Supporting flash only — keep beacon / bird readable
      const alpha = Math.floor(160 * fade);
      drawLightningBolt(p, state.lightning.x, boardH, alpha);
      p.push();
      p.noStroke();
      p.fill(255, 250, 220, Math.floor(28 * fade));
      p.rect(0, 0, boardW, boardH);
      p.pop();
    }
  }
}
